<template>
	<div class="expanded-row-compact">
		<!-- Left: Opportunity details + notes -->
		<div class="details-panel">
			<p class="section-title">{{ t("opportunities-table.expanded-row.section-details") }}</p>

			<div class="details-grid">
				<div v-for="field in detailFields" :key="field.key" class="field">
					<span class="label">{{ detailColumnLabel(field.key) }}</span>
					<span v-if="field.key !== ColumnKey.Parcels" class="value">{{ field.value || "—" }}</span>
					<span v-else-if="dedupedLotNumbers.length" class="value parcels-cell">
						<span class="primary-parcel">{{ dedupedLotNumbers[0] }}</span>
						<span v-if="!isParcelsExpanded && dedupedLotNumbers.length > 1" class="parcels-count">
							+{{ dedupedLotNumbers.length - 1 }}
						</span>
						<span v-if="isParcelsExpanded && dedupedLotNumbers.length > 1" class="parcels-rest">
							, {{ dedupedLotNumbers.slice(1).join(", ") }}
						</span>
						<button
							v-if="dedupedLotNumbers.length > 1"
							type="button"
							class="parcels-toggle"
							@click="isParcelsExpanded = !isParcelsExpanded"
						>
							{{ isParcelsExpanded ? t("parcels.hide") : t("parcels.showAll") }}
						</button>
					</span>
					<span v-else class="value">—</span>
				</div>
			</div>

			<div v-if="showNotesInput" class="notes">
				<div class="note-section">
					<div class="note-header">
						<span class="label">{{
							isPersonalNotesContext
								? t("opportunities-table.expanded-row.my-notes-label")
								: t("opportunities-table.expanded-row.broker-label")
						}}</span>
						<span v-if="!canWriteBrokerNote" class="badge">{{ t("opportunities-table.expanded-row.readonly") }}</span>
					</div>
					<textarea
						v-if="canWriteBrokerNote"
						v-model="brokerNoteText"
						class="textarea"
						:placeholder="t('opportunities-table.expanded-row.note-placeholder')"
						maxlength="5000"
						@input="onBrokerNoteInput"
					/>
					<p v-else class="readonly">{{ notes.brokerNote || "—" }}</p>
					<div v-if="canWriteBrokerNote" class="note-footer">
						<span class="indicator">
							<template v-if="brokerSaved">{{ t("opportunities-table.expanded-row.saved") }} · </template
							>{{ brokerNoteText.length }} / 5000
						</span>
					</div>
				</div>

				<div v-if="isInCcaProject" class="note-section">
					<div class="note-header">
						<span class="label">{{ t("opportunities-table.expanded-row.client-cca-label") }}</span>
						<span v-if="!canWriteClientNote" class="badge">{{ t("opportunities-table.expanded-row.readonly") }}</span>
					</div>
					<textarea
						v-if="canWriteClientNote"
						v-model="clientNoteText"
						class="textarea"
						:placeholder="t('opportunities-table.expanded-row.note-placeholder')"
						maxlength="5000"
						@input="onClientNoteInput"
					/>
					<p v-else class="readonly">{{ notes.clientNote || "—" }}</p>
					<div v-if="canWriteClientNote" class="note-footer">
						<span class="indicator">
							<template v-if="clientSaved">{{ t("opportunities-table.expanded-row.saved") }} · </template
							>{{ clientNoteText.length }} / 5000
						</span>
					</div>
				</div>
			</div>
		</div>

		<!-- Right: Links & Location card -->
		<div v-if="showLinks || showLocation" class="side-card">
			<div v-if="showLocation && (mapboxImageUrl || appOpportunityUrl)" class="card-section">
				<p class="card-section-title">{{ t("opportunities-table.expanded-row.section-location") }}</p>
				<div class="map-preview">
					<div v-if="mapboxImageUrl" class="map-frame">
						<img
							:src="mapboxImageUrl"
							alt=""
							:class="{ loaded: isMapImageLoaded }"
							@load="isMapImageLoaded = true"
							@error="isMapImageLoaded = true"
						/>
						<div v-if="!isMapImageLoaded" class="map-skeleton" />
					</div>
					<a v-if="appOpportunityUrl" :href="appOpportunityUrl" target="_blank" rel="noopener" class="carte-btn">
						{{ t("opportunities-table.expanded-row.map-open") }}
					</a>
				</div>
			</div>

			<div
				v-if="
					showLinks && (isListingLink || item.modelling_link || (item.hubspot_record_id && hasCrmRead) || googleMapsUrl)
				"
				class="card-section"
			>
				<p class="card-section-title">{{ t("opportunities-table.columns.links-data") }}</p>
				<div class="links">
					<a
						v-if="isListingLink"
						:href="item.link!"
						target="_blank"
						rel="noopener"
						class="btn"
						:title="t('opportunities-table.expanded-row.link-landerz-title')"
					>
						<img :src="listingIconDataUri" width="14" height="14" alt="" aria-hidden="true" />
					</a>

					<a
						v-if="item.modelling_link"
						:href="item.modelling_link"
						target="_blank"
						rel="noopener"
						class="btn"
						:title="t('opportunities-table.expanded-row.link-modelling-title')"
					>
						<img :src="giraffeIconUrl" width="14" height="14" alt="" aria-hidden="true" />
					</a>

					<a
						v-if="item.hubspot_record_id && hasCrmRead"
						:href="hubspotDealUrl ?? undefined"
						target="_blank"
						rel="noopener"
						class="btn"
						:title="t('opportunities-table.expanded-row.link-hubspot-title')"
					>
						<AppIcon :name="IconName.HubspotColor" :size="14" />
					</a>

					<a
						v-if="googleMapsUrl"
						:href="googleMapsUrl"
						target="_blank"
						rel="noopener"
						class="btn"
						:title="t('opportunities-table.expanded-row.link-maps-title')"
					>
						<AppIcon :name="IconName.GoogleMaps" :size="14" />
					</a>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from "vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { useDebounceFn } from "@vueuse/core"
import { useI18n } from "vue-i18n"
import { UseRootStore, Permission } from "@/model/RootStore"
import { OpportunityType, type Opportunity } from "@/model/DataModel"
import { buildOpportunityIconDataUri, resolveIconFamily } from "@/utils/opportunityIcons"
import { ColumnKey, formatMovableColumnValue, opportunityColumnLabel } from "@/utils/opportunityColumns"
import giraffeIconUrl from "@/assets/images/giraffe-icon.png"
import { useUnitSystem } from "@/composables/useUnitSystem"

const props = defineProps<{
	item: Opportunity
	detailKeys: string[]
	showNotesInput: boolean
	showLinks: boolean
	showLocation: boolean
	isInCcaProject: boolean
	isPersonalNotesContext: boolean
	isHuntProject: boolean
	projectId?: string
}>()

const listingIconDataUri = buildOpportunityIconDataUri(resolveIconFamily("listing_landerz")!.color)

const isListingLink = computed(() => !!props.item.link && props.item.type === OpportunityType.ListingLanderz)

const { t } = useI18n()
const { areaUnit, areaUnitSymbol, pricePerAreaUnitSymbol } = useUnitSystem()
const store = UseRootStore()

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN
const HUBSPOT_DEAL_URL = import.meta.env.VITE_HUBSPOT_DEAL_ENDPOINT

const notes = computed(
	() => store.opportunityNotes.get(Number(props.item.id)) ?? { brokerNote: null, clientNote: null }
)
const noteCount = computed(() => [notes.value.brokerNote, notes.value.clientNote].filter(n => n != null).length)

function detailFieldValue(key: string): string {
	if (key === ColumnKey.Notes) return noteCount.value > 0 ? String(noteCount.value) : ""

	return formatMovableColumnValue(key as ColumnKey, props.item, t, areaUnit.value)
}

function detailColumnLabel(key: string): string {
	return opportunityColumnLabel(
		key,
		t,
		areaUnitSymbol.value,
		pricePerAreaUnitSymbol.value,
		props.item.collaborative_broker_names?.filter(Boolean).length
	)
}

const detailFields = computed(() =>
	props.detailKeys
		.filter(key => key !== ColumnKey.Notes || props.isHuntProject)
		.map(key => ({ key, value: detailFieldValue(key) }))
)

// lot_numbers_full can repeat the primary lot number; dedupe so the count/expanded list don't double-count it
const dedupedLotNumbers = computed<number[]>(() => Array.from(new Set(props.item.lot_numbers_full ?? [])))
const isParcelsExpanded = ref(false)

const brokerNoteText = ref(notes.value.brokerNote ?? "")
const clientNoteText = ref(notes.value.clientNote ?? "")
const brokerSaved = ref(false)
const clientSaved = ref(false)

watch(
	() => props.item.id,
	() => {
		brokerNoteText.value = notes.value.brokerNote ?? ""
		clientNoteText.value = notes.value.clientNote ?? ""
		brokerSaved.value = false
		clientSaved.value = false
	}
)

const canWriteBrokerNote = computed(
	() => props.isPersonalNotesContext || (store.currentUser?.has(Permission.OpportunitySensitiveWrite) ?? false)
)
const canWriteClientNote = computed(
	() => props.isInCcaProject && (store.currentUser?.has(Permission.OpportunityClientNoteWrite) ?? false)
)
const hasCrmRead = computed(() => store.currentUser?.has(Permission.CrmRead) ?? false)

const hubspotDealUrl = computed(() =>
	props.item.hubspot_record_id ? `${HUBSPOT_DEAL_URL.replace(/\/$/, "")}/${props.item.hubspot_record_id}` : null
)

const googleMapsUrl = computed(() => {
	const { latitude, longitude } = props.item
	if (latitude == null || longitude == null) return null
	return `https://www.google.com/maps/@${latitude},${longitude},17z`
})

const appOpportunityUrl = computed(() => {
	if (!props.item.hubspot_record_id) return null
	const url = new URL(`${window.location.origin}/`)
	url.searchParams.set("opp", props.item.hubspot_record_id)
	if (props.projectId) url.searchParams.set("project", props.projectId)
	return url.toString()
})

const mapboxImageUrl = computed(() => {
	const { latitude, longitude } = props.item
	if (latitude == null || longitude == null || !MAPBOX_TOKEN) return null
	return `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/static/${longitude},${latitude},16/560x360?access_token=${MAPBOX_TOKEN}`
})

const isMapImageLoaded = ref(false)

watch(mapboxImageUrl, () => {
	isMapImageLoaded.value = false
})

const debouncedSaveBrokerNote = useDebounceFn(async () => {
	await store.updateOpportunityEvaluation(Number(props.item.id), { brokerNote: brokerNoteText.value }, props.projectId)
	brokerSaved.value = true
}, 800)

const debouncedSaveClientNote = useDebounceFn(async () => {
	await store.updateOpportunityEvaluation(Number(props.item.id), { clientNote: clientNoteText.value }, props.projectId)
	clientSaved.value = true
}, 800)

function onBrokerNoteInput() {
	brokerSaved.value = false
	debouncedSaveBrokerNote()
}

function onClientNoteInput() {
	clientSaved.value = false
	debouncedSaveClientNote()
}
</script>

<style scoped>
.expanded-row-compact {
	display: flex;
	gap: 0;
	background: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-lg);
	overflow: hidden;
}

.details-panel {
	flex: 1;
	min-width: 0;
	padding: var(--spacing-md) var(--space-20);
	display: flex;
	flex-direction: column;
	gap: var(--spacing-md);
}

.section-title {
	margin: 0;
	font-size: var(--font-size-xs);
	font-weight: var(--font-weight-medium);
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: var(--neutral-600);
}

.details-grid {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	column-gap: var(--spacing-lg);
	row-gap: var(--spacing-md);

	.field {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
		min-width: 0;

		.label {
			font-size: var(--font-size-xs);
			color: var(--neutral-600);
		}

		.value {
			font-size: var(--font-size-regular);
			color: var(--neutral-900);
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.value.parcels-cell {
			overflow: visible;
			white-space: normal;

			.parcels-count {
				font-size: var(--font-size-xs);
				color: var(--neutral-600);
			}

			.parcels-toggle {
				display: inline;
				margin-left: var(--space-4);
				background: none;
				border: none;
				padding: 0;
				font: inherit;
				font-size: var(--font-size-xs);
				font-weight: var(--font-weight-semibold);
				color: var(--neutral-600);
				cursor: pointer;

				&:hover {
					color: var(--primary-green);
				}
			}
		}
	}
}

.notes {
	display: flex;
	flex-wrap: wrap;
	gap: var(--spacing-md);

	.note-section {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
		flex: 0 1 calc(50% - var(--spacing-md) / 2);
	}

	.note-header {
		display: flex;
		align-items: center;
		gap: var(--space-6);

		.label {
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-medium);
			color: var(--neutral-600);
		}

		.badge {
			font-size: var(--font-size-xs);
			color: var(--neutral-600);
			background: var(--neutral-50);
			border-radius: var(--border-radius-sm);
			padding: 1px var(--space-4);
		}
	}

	.textarea {
		resize: vertical;
		min-height: 56px;
		padding: var(--spacing-sm) var(--space-10);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-sm);
		font: inherit;
		font-size: 13px;
		line-height: 1.5;
		background: var(--neutral-0);

		&:focus {
			outline: none;
			border-color: var(--neutral-900);
		}
	}

	.readonly {
		font-size: 13px;
		color: var(--neutral-900);
		margin: 0;
		white-space: pre-wrap;
	}

	.note-footer {
		display: flex;
		justify-content: flex-end;

		.indicator {
			font-size: var(--font-size-xs);
			color: var(--neutral-600);
		}
	}
}

.side-card {
	width: 320px;
	flex-shrink: 0;
	margin: var(--spacing-md) var(--space-20) var(--spacing-md) 0;
	padding: var(--spacing-sm) var(--spacing-md);
	background: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-lg);
	display: flex;
	flex-direction: column;
	gap: var(--spacing-sm);

	.links {
		display: flex;
		gap: var(--spacing-xs);
		flex-wrap: wrap;

		.btn {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 28px;
			height: 28px;
			border-radius: var(--border-radius-full);
			border: var(--border-width-thin) solid var(--neutral-200);
			background: var(--neutral-0);
			color: var(--neutral-600);
			text-decoration: none;
			transition: background 0.1s;

			&:hover {
				background: var(--neutral-50);
			}
		}
	}

	.card-section {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
	}

	.card-section-title {
		margin: 0;
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-medium);
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: var(--neutral-600);
	}

	.map-preview {
		position: relative;
		border-radius: var(--border-radius-base);
		overflow: hidden;
		background: var(--neutral-50);

		.map-frame {
			position: relative;
			width: 100%;
			aspect-ratio: 560 / 360;

			img {
				display: block;
				width: 100%;
				height: 100%;
				object-fit: cover;
				opacity: 0;
				transition: opacity 0.15s ease;

				&.loaded {
					opacity: 1;
				}
			}

			.map-skeleton {
				position: absolute;
				inset: 0;
				background: linear-gradient(90deg, var(--neutral-100) 25%, var(--neutral-50) 37%, var(--neutral-100) 63%);
				background-size: 400% 100%;
				animation: map-skeleton-shimmer 1.4s ease infinite;
			}
		}

		.carte-btn {
			position: absolute;
			bottom: var(--spacing-sm);
			right: var(--spacing-sm);
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-medium);
			background: var(--neutral-0);
			color: var(--neutral-900);
			padding: var(--space-4) var(--spacing-sm);
			border-radius: var(--border-radius-full);
			text-decoration: none;

			&:hover {
				background: var(--neutral-50);
			}
		}
	}
}

@keyframes map-skeleton-shimmer {
	0% {
		background-position: 100% 0;
	}
	100% {
		background-position: 0 0;
	}
}
</style>
